import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../services/api';
import Modal from './ui/Modal';
import Button from './ui/Button';
import { FormField, FormGrid, Input, Select, Textarea } from './ui/Form';

const FORM_ID = 'meeting-form';

const createMeeting = async (data) => {
    const res = await api.post('/meetings', data);
    return res.data;
};

const MeetingModal = ({ isOpen, onClose, initialDate }) => {
    const queryClient = useQueryClient();

    const [formData, setFormData] = useState({
        title: '',
        meeting_date: '',
        start_time: '09:00',
        end_time: '10:00',
        description: '',
        meeting_link: '',
        venue: '',
        meeting_type: 'face to face'
    });

    useEffect(() => {
        if (initialDate) {
            setFormData(prev => ({ ...prev, meeting_date: initialDate }));
        }
    }, [initialDate]);

    const mutation = useMutation({
        mutationFn: createMeeting,
        onSuccess: () => {
            queryClient.invalidateQueries(['calendarEvents']);
            queryClient.invalidateQueries(['meetings']);
            toast.success('Meeting registered');
            onClose();
        },
        onError: (error) => {
            toast.error(error.response?.data?.message || 'Failed to register meeting');
        }
    });

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        if (!formData.title || !formData.meeting_date || !formData.start_time || !formData.end_time) {
            toast.error('Add a title, date and time before registering the meeting');
            return;
        }
        mutation.mutate(formData);
    };

    const isSaving = mutation.isPending ?? mutation.isLoading;

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            size="md"
            title="Register ICT meeting"
            subtitle="The meeting appears on the shared calendar once registered."
            footer={
                <>
                    <Button variant="secondary" onClick={onClose} disabled={isSaving}>
                        Cancel
                    </Button>
                    <Button type="submit" form={FORM_ID} variant="primary" loading={isSaving}>
                        Register meeting
                    </Button>
                </>
            }
        >
            <form id={FORM_ID} onSubmit={handleSubmit} className="space-y-4">
                <FormField label="Meeting title" required htmlFor="meeting-title">
                    <Input
                        id="meeting-title"
                        name="title"
                        value={formData.title}
                        onChange={handleChange}
                        required
                        placeholder="e.g. Infrastructure review"
                    />
                </FormField>

                <FormGrid>
                    <FormField label="Date" required htmlFor="meeting-date">
                        <Input
                            id="meeting-date"
                            type="date"
                            name="meeting_date"
                            value={formData.meeting_date}
                            onChange={handleChange}
                            required
                        />
                    </FormField>

                    <FormField label="Meeting type" htmlFor="meeting-type">
                        <Select
                            id="meeting-type"
                            name="meeting_type"
                            value={formData.meeting_type}
                            onChange={handleChange}
                        >
                            <option value="face to face">Face to face</option>
                            <option value="virtual">Virtual</option>
                        </Select>
                    </FormField>
                </FormGrid>

                <FormGrid>
                    <FormField label="Start time" required htmlFor="meeting-start">
                        <Input
                            id="meeting-start"
                            type="time"
                            name="start_time"
                            value={formData.start_time}
                            onChange={handleChange}
                            required
                        />
                    </FormField>

                    <FormField label="End time" required htmlFor="meeting-end">
                        <Input
                            id="meeting-end"
                            type="time"
                            name="end_time"
                            value={formData.end_time}
                            onChange={handleChange}
                            required
                        />
                    </FormField>
                </FormGrid>

                {formData.meeting_type === 'virtual' ? (
                    <FormField
                        label="Meeting link"
                        htmlFor="meeting-link"
                        hint="Attendees open this link to join."
                    >
                        <Input
                            id="meeting-link"
                            type="url"
                            name="meeting_link"
                            value={formData.meeting_link}
                            onChange={handleChange}
                            placeholder="https://…"
                        />
                    </FormField>
                ) : (
                    <FormField label="Venue" htmlFor="meeting-venue">
                        <Input
                            id="meeting-venue"
                            name="venue"
                            value={formData.venue}
                            onChange={handleChange}
                            placeholder="e.g. Conference Room A"
                        />
                    </FormField>
                )}

                <FormField label="Agenda" htmlFor="meeting-description">
                    <Textarea
                        id="meeting-description"
                        name="description"
                        value={formData.description}
                        onChange={handleChange}
                        rows={3}
                        placeholder="Topics to cover and any preparation required."
                    />
                </FormField>
            </form>
        </Modal>
    );
};

export default MeetingModal;
